import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logoImage from '../../assets/logoIcon.png';

function Sidebar({ admin, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState({});

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: '📊',
      path: '/admin/dashboard'
    },
    {
      id: 'users',
      label: 'User Management',
      icon: '👥',
      path: '/admin/users',
      subItems: [
        { label: 'All Users', path: '/admin/users' },
        { label: 'Sellers', path: '/admin/sellers' },
        { label: 'Buyers', path: '/admin/users?role=buyer' }
      ]
    },
    {
      id: 'listings',
      label: 'Listings',
      icon: '📦',
      path: '/admin/listings',
      subItems: [
        { label: 'All Listings', path: '/admin/listings' },
        { label: 'Pending Approval', path: '/admin/listings/moderation' },
        { label: 'Featured', path: '/admin/listings?is_featured=true' }
      ]
    },
    {
      id: 'orders',
      label: 'Orders',
      icon: '🛒',
      path: '/admin/orders',
      subItems: [
        { label: 'All Orders', path: '/admin/orders' },
        { label: 'Pending Orders', path: '/admin/orders?status=pending' },
        { label: 'Disputes', path: '/admin/orders/disputes' } 
      ]
    },
    {
      id: 'escrow',
      label: 'Escrow',
      icon: '🔒',
      path: '/admin/escrows',
      subItems: [
        { label: 'All Escrows', path: '/admin/escrows' },
        { label: 'Active Escrows', path: '/admin/escrows?status=held' },
        { label: 'Disputed Escrows', path: '/admin/escrows/disputes' },
        { label: 'Statistics', path: '/admin/escrows/statistics' }
      ]
    },
    {
      id: 'finance',
      label: 'Finance',
      icon: '💰',
      path: '/admin/finance/transactions',
      subItems: [
        { label: 'Transactions', path: '/admin/finance/transactions' },
        { label: 'Payments', path: '/admin/finance/payments' },
        { label: 'Wallets', path: '/admin/finance/wallets' },
        { label: 'Fee Settings', path: '/admin/finance/fee-settings' },
        { label: 'Fee Analytics', path: '/admin/finance/fee-analytics' },
        { label: 'Fee Transactions', path: '/admin/finance/fee-transactions' },
        { label: 'Reports', path: '/admin/finance/reports' }
      ]
    },
    {
      id: 'boost',
      label: 'Boost Management',
      icon: '🚀',
      path: '/admin/boost/packages',
      subItems: [
        { label: 'Packages', path: '/admin/boost/packages' },
        { label: 'Orders', path: '/admin/boost/orders' },
        { label: 'Boosted Listings', path: '/admin/boost/listings' },
        { label: 'Analytics', path: '/admin/boost/analytics' }
      ]
    },
    {
      id: 'categories',
      label: 'Categories',
      icon: '📑',
      path: '/admin/categories',
      subItems: [
        { label: 'Manage Categories', path: '/admin/categories' },
        { label: 'Analytics', path: '/admin/categories/analytics' }
      ]
    },
    {
      id: 'system',
      label: 'System',
      icon: '⚙️',
      path: '/admin/system/admins',
      subItems: [
        { label: 'Admin Users', path: '/admin/system/admins' },
        { label: 'Activity Logs', path: '/admin/system/activities' },
        { label: 'Settings', path: '/admin/system/settings' },
        { label: 'System Health', path: '/admin/system/health' }
      ]
    },
    {
      id: 'disputes',
      label: 'Disputes',
      icon: '⚖️',
      path: '/admin/disputes',
      subItems: [
        { label: 'All Disputes', path: '/admin/disputes' },
        { label: 'Statistics', path: '/admin/disputes/stats' }
      ]
    },
    {
      id: 'support',
      label: 'Support',
      icon: '💬',
      path: '/admin/support/tickets',
      subItems: [
        { label: 'All Tickets', path: '/admin/support/tickets' },
        { label: 'Statistics', path: '/admin/support/stats' },
        { label: 'Categories', path: '/admin/support/categories' }
      ]
    },
    {
      id: 'reviews',
      label: 'Reviews',
      icon: '⭐',
      path: '/admin/reviews',
      subItems: [
        { label: 'All Reviews', path: '/admin/reviews' },
        { label: 'Pending Reviews', path: '/admin/reviews?status=pending' },
        { label: 'Approved Reviews', path: '/admin/reviews?status=approved' },
        { label: 'Rejected Reviews', path: '/admin/reviews?status=rejected' },
        { label: 'Statistics', path: '/admin/reviews/stats' }
      ]
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: '⚠️',
      path: '/admin/reports/listings',
      subItems: [
        { label: 'Reported Listings', path: '/admin/reports/listings' },
        { label: 'Reported Sellers', path: '/admin/reports/sellers' },
        { label: 'Statistics', path: '/admin/reports/stats' }
      ]
    },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigation = (item) => {
    if (item.subItems) {
      setExpandedItems(prev => ({
        ...prev,
        [item.id]: !prev[item.id]
      }));
      // Only navigate to main path if not already on a sub-item path
      if (!isActive(item.path) && !location.pathname.startsWith(item.path + '/')) {
        navigate(item.path);
      }
    } else {
      navigate(item.path);
    }
  };

  const handleSubItemNavigation = (path, parentId) => {
    navigate(path);
    // Keep parent expanded when clicking sub-item
    setExpandedItems(prev => ({
      ...prev,
      [parentId]: true
    }));
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      onLogout();
    }
  };

  return (
    <div className="w-64 bg-gray-900 text-white flex flex-col h-screen">
      {/* Logo Section with Brand Color */}
      <div className="p-6 border-b border-gray-800">
  <div className="flex items-center space-x-3">
    <div className="w-10 h-10 bg-[#ff660e] rounded-xl flex items-center justify-center shadow-lg shadow-[#ff660e]/20 overflow-hidden">
      <img 
        src={logoImage} 
        alt="EcoMart Logo" 
        className="w-full h-full object-cover"
      />
    </div>
    <div>
      <h1 className="text-xl font-bold text-white">EcoMart</h1>
      <p className="text-xs text-gray-400">Admin Panel</p>
    </div>
  </div>
</div>

      {/* Admin Info */}
      <div className="p-4 border-b border-gray-800 bg-gray-850">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#ff660e] to-orange-300 rounded-full flex items-center justify-center">
            <span className="text-white font-semibold text-sm">
              {admin?.first_name?.[0]}{admin?.last_name?.[0]}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {admin?.first_name} {admin?.last_name}
            </p>
            <p className="text-xs text-gray-400 truncate">{admin?.email}</p>
            <p className="text-xs text-[#ff660e] font-medium capitalize">
              {admin?.role?.replace('_', ' ')}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        {menuItems.map((item) => {
          const isItemActive = isActive(item.path) || expandedItems[item.id];
          const isExpanded = expandedItems[item.id];
          
          return (
            <div key={item.id} className="mb-1">
              <button
                onClick={() => handleNavigation(item)}
                className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left transition-all duration-200 group ${
                  isItemActive
                    ? 'bg-[#ff660e] text-white shadow-md'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <span className={`text-lg transition-transform group-hover:scale-110 ${
                  isItemActive ? 'text-white' : 'text-gray-400'
                }`}>
                  {item.icon}
                </span>
                <span className="font-medium flex-1">{item.label}</span>
                {item.subItems && (
                  <span className={`transform transition-transform duration-200 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}>
                    ▼
                  </span>
                )}
              </button>

              {/* Sub-items */}
              {item.subItems && isExpanded && (
                <div className="ml-2 mt-1 space-y-1 pl-8 border-l border-gray-700">
                  {item.subItems.map((subItem, index) => {
                    const isSubItemActive = location.pathname === subItem.path || 
                      (subItem.path.includes('?') && location.search === subItem.path.split('?')[1]);
                    
                    return (
                      <button
                        key={index}
                        onClick={() => handleSubItemNavigation(subItem.path, item.id)}
                        className={`w-full flex items-center space-x-2 px-3 py-2 rounded text-sm transition-all duration-150 ${
                          isSubItemActive
                            ? 'text-[#ff660e] bg-gray-800 font-medium'
                            : 'text-gray-400 hover:text-white hover:bg-gray-800'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          isSubItemActive ? 'bg-[#ff660e]' : 'bg-gray-600'
                        }`}></span>
                        <span>{subItem.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer Section */}
      <div className="p-4 border-t border-gray-800 space-y-2 bg-gray-850">
        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-white hover:bg-red-600/20 hover:text-red-400 transition-colors mt-2 border border-red-900/50 group"
        >
          <span className="text-lg group-hover:rotate-90 transition-transform">🚪</span>
          <span className="font-medium">Logout</span>
        </button>

        {/* Version Info */}
        <div className="pt-2 border-t border-gray-800">
          <p className="text-xs text-gray-500 text-center">
            EcoMart Admin v1.0.0
          </p>
          <div className="flex items-center justify-center mt-1">
            <div className="w-2 h-2 bg-[#ff660e] rounded-full mr-2 animate-pulse"></div>
            <span className="text-xs text-gray-400">All systems operational</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;